/**
 * Animation optimization utilities for smooth 60fps performance
 */

// Animation frame scheduler for smooth animations
export class AnimationScheduler {
  private static rafId: number | null = null
  private static callbacks: Array<() => void> = []
  
  // Schedule callback for next animation frame
  static schedule(callback: () => void): void {
    this.callbacks.push(callback)
    
    if (!this.rafId) {
      this.rafId = requestAnimationFrame(() => {
        const callbacks = [...this.callbacks]
        this.callbacks = []
        this.rafId = null
        
        callbacks.forEach(cb => {
          try {
            cb()
          } catch (error) {
            console.error('Animation callback error:', error)
          }
        })
      })
    }
  }
  
  // Cancel scheduled animations
  static cancel(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
      this.callbacks = []
    }
  }
}

// Smooth scroll utility
export class SmoothScroll {
  private static isScrolling = false
  
  // Smooth scroll to element
  static toElement(
    element: HTMLElement, 
    options: {
      duration?: number
      offset?: number
      behavior?: 'smooth' | 'auto'
    } = {}
  ): Promise<void> {
    const { duration = 800, offset = 0, behavior = 'smooth' } = options
    
    return new Promise((resolve) => {
      if (this.isScrolling) {
        resolve()
        return
      }
      
      this.isScrolling = true
      
      const targetPosition = element.offsetTop - offset
      const startPosition = window.pageYOffset
      const distance = targetPosition - startPosition
      
      if (behavior === 'smooth' && 'scrollBehavior' in document.documentElement.style) {
        // Use native smooth scroll if supported
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        })
        
        setTimeout(() => {
          this.isScrolling = false
          resolve()
        }, duration)
      } else {
        // Custom smooth scroll implementation
        let startTime: number | null = null
        
        const animateScroll = (currentTime: number) => {
          if (startTime === null) startTime = currentTime
          const timeElapsed = currentTime - startTime
          const progress = Math.min(timeElapsed / duration, 1)
          
          // Easing function (ease-out-cubic)
          const ease = 1 - Math.pow(1 - progress, 3)
          
          window.scrollTo(0, startPosition + distance * ease)
          
          if (progress < 1) {
            requestAnimationFrame(animateScroll)
          } else {
            this.isScrolling = false
            resolve()
          }
        }
        
        requestAnimationFrame(animateScroll)
      }
    })
  }
  
  // Smooth scroll to top
  static toTop(duration = 600): Promise<void> {
    return new Promise((resolve) => {
      if (this.isScrolling) {
        resolve()
        return
      }
      
      this.isScrolling = true
      const startPosition = window.pageYOffset
      let startTime: number | null = null
      
      const animateScroll = (currentTime: number) => {
        if (startTime === null) startTime = currentTime
        const timeElapsed = currentTime - startTime
        const progress = Math.min(timeElapsed / duration, 1)
        
        // Easing function
        const ease = 1 - Math.pow(1 - progress, 3)
        
        window.scrollTo(0, startPosition * (1 - ease))
        
        if (progress < 1) {
          requestAnimationFrame(animateScroll)
        } else {
          this.isScrolling = false
          resolve()
        }
      }
      
      requestAnimationFrame(animateScroll)
    })
  }
}

// Performance-optimized transitions
export class TransitionOptimizer {
  // Optimize element for animations
  static optimizeForAnimation(element: HTMLElement): void {
    element.style.willChange = 'transform, opacity'
    element.style.backfaceVisibility = 'hidden'
    element.style.perspective = '1000px'
  }
  
  // Clean up animation optimizations
  static cleanupAnimation(element: HTMLElement): void {
    element.style.willChange = 'auto'
    element.style.backfaceVisibility = ''
    element.style.perspective = ''
  }
  
  // Batch DOM reads and writes
  static batchDOMOperations(
    reads: Array<() => void>,
    writes: Array<() => void>
  ): void {
    // Batch all reads first
    reads.forEach(read => read())
    
    // Then batch all writes in next frame
    AnimationScheduler.schedule(() => {
      writes.forEach(write => write())
    })
  }
  
  // Debounce expensive operations
  static debounce<T extends (...args: any[]) => void>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout
    
    return (...args: Parameters<T>) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func(...args), wait)
    }
  }
  
  // Throttle high-frequency events
  static throttle<T extends (...args: any[]) => void>(
    func: T,
    limit: number
  ): (...args: Parameters<T>) => void {
    let inThrottle: boolean
    
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func(...args)
        inThrottle = true
        setTimeout(() => inThrottle = false, limit)
      }
    }
  }
}

// Intersection observer for performance
export class VisibilityOptimizer {
  private static observers = new Map<string, IntersectionObserver>()
  
  // Create optimized intersection observer
  static createObserver(
    callback: (entries: IntersectionObserverEntry[]) => void,
    options: IntersectionObserverInit = {}
  ): IntersectionObserver {
    const defaultOptions = {
      rootMargin: '50px 0px',
      threshold: [0, 0.25, 0.5, 0.75, 1],
      ...options
    }
    
    return new IntersectionObserver(callback, defaultOptions)
  }
  
  // Observe element visibility for animations
  static observeForAnimation(
    element: HTMLElement,
    onVisible: () => void,
    onHidden?: () => void
  ): () => void {
    const observer = this.createObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          TransitionOptimizer.optimizeForAnimation(entry.target as HTMLElement)
          onVisible()
        } else if (onHidden) {
          TransitionOptimizer.cleanupAnimation(entry.target as HTMLElement)
          onHidden()
        }
      })
    })
    
    observer.observe(element)
    
    // Return cleanup function
    return () => {
      observer.unobserve(element)
      observer.disconnect()
    }
  }
  
  // Lazy load animations
  static lazyLoadAnimation(
    element: HTMLElement,
    animationClass: string
  ): () => void {
    return this.observeForAnimation(
      element,
      () => {
        element.classList.add(animationClass)
      },
      () => {
        element.classList.remove(animationClass)
      }
    )
  }
}

// CSS animation utilities
export class CSSAnimationUtils {
  // Create optimized keyframes
  static createKeyframes(name: string, keyframes: Record<string, any>): void {
    const keyframeRule = Object.entries(keyframes)
      .map(([key, value]) => {
        const properties = Object.entries(value)
          .map(([prop, val]) => `${prop}: ${val}`)
          .join('; ')
        return `${key} { ${properties} }`
      })
      .join(' ')
    
    const style = document.createElement('style')
    style.textContent = `@keyframes ${name} { ${keyframeRule} }`
    document.head.appendChild(style)
  }
  
  // Optimize CSS transforms
  static optimizeTransform(element: HTMLElement, transform: string): void {
    // Use transform3d to trigger hardware acceleration
    const optimizedTransform = transform.includes('translate3d') 
      ? transform 
      : transform.replace(/translate\(([^)]+)\)/, 'translate3d($1, 0)')
    
    element.style.transform = optimizedTransform
  }
  
  // Create staggered animations
  static staggerAnimation(
    elements: HTMLElement[],
    animationClass: string,
    delay = 100
  ): void {
    elements.forEach((element, index) => {
      setTimeout(() => {
        element.classList.add(animationClass)
      }, index * delay)
    })
  }
}

// Performance monitoring for animations
export class AnimationPerformanceMonitor {
  private static frameCount = 0
  private static lastTime = performance.now()
  private static fps = 60
  
  // Monitor FPS
  static startFPSMonitoring(): () => void {
    const monitor = () => {
      this.frameCount++
      const currentTime = performance.now()
      
      if (currentTime >= this.lastTime + 1000) {
        this.fps = Math.round((this.frameCount * 1000) / (currentTime - this.lastTime))
        this.frameCount = 0
        this.lastTime = currentTime
        
        // Log warning if FPS drops below 50
        if (this.fps < 50) {
          console.warn(`⚠️ Low FPS detected: ${this.fps}fps`)
        }
      }
      
      requestAnimationFrame(monitor)
    }
    
    const rafId = requestAnimationFrame(monitor)
    
    // Return cleanup function
    return () => cancelAnimationFrame(rafId)
  }
  
  // Get current FPS
  static getCurrentFPS(): number {
    return this.fps
  }
  
  // Check if device supports smooth animations
  static supportsHardwareAcceleration(): boolean {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
    return !!gl
  }
  
  // Reduce animations for low-performance devices
  static shouldReduceAnimations(): boolean {
    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return true
    }
    
    // Check for low-end device indicators
    const connection = (navigator as any).connection
    if (connection && connection.saveData) {
      return true
    }
    
    // Check hardware capabilities
    if (!this.supportsHardwareAcceleration()) {
      return true
    }
    
    // Check if FPS is consistently low
    return this.fps < 45
  }
}